// Nostr key management: generate, login, sign, account storage.
// Ported from ui.ts, using npm nostr-tools instead of CDN.

import { generateSecretKey, getPublicKey, finalizeEvent } from 'nostr-tools/pure';
import { nsecEncode, npubEncode, decode as nip19Decode } from 'nostr-tools/nip19';
import type { NostrEvent, SavedAccount } from './types';

// ─── localStorage keys (backward-compatible with ui.ts) ───

export const ACCOUNTS_KEY = 'nomail_accounts';
export const LAST_ACCOUNT_KEY = 'nomail_last_account';

// ─── Account storage ───

export function getAccounts(): SavedAccount[] {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function saveAccount(pubkey: string, nsec: string): void {
  const accounts = getAccounts();
  const npub = npubEncode(pubkey);
  const i = accounts.findIndex((a) => a.pubkey === pubkey);
  if (i >= 0) {
    accounts[i].nsec = nsec;
    accounts[i].npub = npub;
  } else {
    accounts.push({ pubkey, nsec, npub, addedAt: Date.now() });
  }
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

export function removeAccount(pubkey: string): void {
  const accounts = getAccounts().filter((a) => a.pubkey !== pubkey);
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

// ─── Migration from old single-key format ───

export function migrateOldKey(): void {
  const old = localStorage.getItem('nomail_nsec');
  if (!old || !old.startsWith('nsec1')) return;
  try {
    const decoded = nip19Decode(old);
    if (decoded.type !== 'nsec') return;
    saveAccount(getPublicKey(decoded.data as Uint8Array), old);
    localStorage.removeItem('nomail_nsec');
  } catch {
    // ignore
  }
}

// ─── Key generation ───

export interface GeneratedKey {
  secretKey: Uint8Array;
  pubkey: string;
  nsec: string;
  npub: string;
}

export function generateKey(): GeneratedKey {
  const sk = generateSecretKey();
  const pk = getPublicKey(sk);
  const nsec = nsecEncode(sk);
  const npub = npubEncode(pk);
  return { secretKey: sk, pubkey: pk, nsec, npub };
}

// ─── nsec login ───

export interface DecodedNsec {
  secretKey: Uint8Array;
  pubkey: string;
  nsec: string;
  npub: string;
}

export function decodeNsec(nsec: string): DecodedNsec {
  const decoded = nip19Decode(nsec);
  if (decoded.type !== 'nsec') throw new Error('Not a valid nsec');
  const sk = decoded.data as Uint8Array;
  const pk = getPublicKey(sk);
  return {
    secretKey: sk,
    pubkey: pk,
    nsec,
    npub: npubEncode(pk),
  };
}

// ─── Auth event signing (local nsec) ───

export function signAuthEvent(
  secretKey: Uint8Array,
  nonce: string,
): NostrEvent {
  const tmpl = {
    kind: 1,
    content: nonce,
    tags: [['challenge', nonce]] as string[][],
    created_at: Math.floor(Date.now() / 1000),
  };
  return finalizeEvent(tmpl, secretKey);
}

// ─── Auth event signing (NIP-07 extension) ───

export async function signAuthEventWithExtension(
  nonce: string,
): Promise<NostrEvent> {
  if (!window.nostr) throw new Error('No Nostr extension found');
  const tmpl = {
    kind: 1,
    content: nonce,
    tags: [['challenge', nonce]] as string[][],
    created_at: Math.floor(Date.now() / 1000),
  };
  return window.nostr.signEvent(tmpl);
}

// ─── Auth method detection ───

export function isExtensionAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.nostr;
}
